/**
 * Hub de disparos da administracao: o retorno de TODOS os disparos, por onde
 * a mensagem sai e onde esta o dinheiro parado.
 *
 * Hierarquia, de cima para baixo:
 *  1. Identidade e janela do cabecalho.
 *  2. Retorno dos disparos (WhatsApp + e-mail) contra o periodo anterior, com
 *     a projecao de 30 dias no ritmo atual.
 *  3. Os canais (WhatsApp, e-mail e a ligacao em lote que ainda nao existe),
 *     cada um com o proprio resumo do periodo.
 *  4. "O que fazer agora" (derivado dos dados) ao lado do ranking de
 *     empresas.
 *  5. Radar de oportunidades: publicos pos-fato parados em cada empresa, com
 *     retorno esperado e atalho para preparar o envio.
 *
 * Decisoes nao obvias:
 *  - Dois recursos (envios de todas as empresas e campanhas de e-mail): a
 *    falha de um nao derruba o outro. O cartao do canal com erro diz "sem
 *    resumo" e o botao "Abrir" continua la -- ferramenta de disparo precisa
 *    abrir mesmo com o agregado fora do ar.
 *  - "Preparar envio" (radar, ranking, insight) troca a empresa em foco e
 *    abre o WhatsApp com `?segmento=`, preservando o periodo da URL.
 *  - "Ligação da IA em lote" existe como cartao para o dono ver o que vem,
 *    mas sem link: RF08 (teto de 2 ligacoes por lead) precisa ser repensado
 *    antes de ligar em massa.
 *  - Toda leitura parte de um fato ja ocorrido. Nenhum publico aqui e "toda
 *    a base" ou "vai vencer": sem o fato nao ha comissao.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Footnote, HeroBanner } from '../../../components/ui';
import { useApiResource, type AsyncState } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatBRLFromCents, formatCount, formatDate } from '../../../lib/format';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import { fetchBroadcasts, fetchEmailCampaigns } from '../api';
import { TOTAIS_VAZIOS, diasEntre, porUnidade, projetar, totaisAnteriores, totalizarEnvios, type Totais } from '../comparar';
import { CartaoCanal } from '../components/CartaoCanal';
import { GradeDeRetorno } from '../components/GradeDeRetorno';
import { OQueFazerAgora } from '../components/OQueFazerAgora';
import { RadarDeOportunidades } from '../components/RadarDeOportunidades';
import { RankingDeEmpresas, type LinhaDoRanking } from '../components/RankingDeEmpresas';
import { insightsDoHub, type AcaoInsight } from '../insights';
import { medidasDeRetorno } from '../medidas';
import type { BroadcastsData, EmailCampaign, EmailCampaignsData, EmailTotals, SegmentKey } from '../types';

function custoDoEmail(aceitos: number, porMilCents: number): number {
  return Math.round((aceitos * porMilCents) / 1_000);
}

/** Soma WhatsApp e e-mail no formato de `Totais` (e-mail nao tem resposta nem "fechado por"). */
function combinar(wa: Totais | null, email: EmailTotals | null, porMilCents: number): Totais {
  const w = wa ?? TOTAIS_VAZIOS;
  if (email === null) {
    return w;
  }
  return {
    ...w,
    broadcasts: w.broadcasts + email.campaigns,
    paid: w.paid + email.paid,
    recoveredAmountCents: w.recoveredAmountCents + email.recoveredAmountCents,
    commissionCents: w.commissionCents + email.commissionCents,
    costCents: w.costCents + custoDoEmail(email.accepted, porMilCents),
  };
}

function totaisDoEmail(campanhas: readonly EmailCampaign[]): EmailTotals {
  return campanhas.reduce<EmailTotals>(
    (acc, c) => ({
      campaigns: acc.campaigns + 1,
      accepted: acc.accepted + c.acceptedCount,
      failed: acc.failed + c.failedCount,
      opened: acc.opened + c.openedCount,
      clicked: acc.clicked + c.clickedCount,
      unsubscribed: acc.unsubscribed + c.unsubscribedCount,
      paid: acc.paid + c.paidCount,
      recoveredAmountCents: acc.recoveredAmountCents + c.recoveredAmountCents,
      commissionCents: acc.commissionCents + c.commissionCents,
    }),
    { campaigns: 0, accepted: 0, failed: 0, opened: 0, clicked: 0, unsubscribed: 0, paid: 0, recoveredAmountCents: 0, commissionCents: 0 },
  );
}

export function Disparos(): JSX.Element {
  const { companies, selectCompany } = useCompany();
  const { range, selection } = usePeriod();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = 'Disparos · Converte Fácil';
  }, []);

  const buscarEnvios = useCallback((signal: AbortSignal) => fetchBroadcasts(null, range, signal), [range]);
  const buscarEmails = useCallback((signal: AbortSignal) => fetchEmailCampaigns(range, signal), [range]);
  const envios = useApiResource(buscarEnvios, 60_000);
  const emails = useApiResource(buscarEmails, 60_000);

  const dadosEnvios = envios.state.status === 'success' ? envios.state.data : null;
  const dadosEmails = emails.state.status === 'success' ? emails.state.data : null;
  const simulado = (envios.state.status === 'success' && envios.state.simulated) || (emails.state.status === 'success' && emails.state.simulated);
  const periodoTexto = describeSelection(selection);

  const resumo = useMemo(() => {
    if (dadosEnvios === null && dadosEmails === null) {
      return null;
    }
    const porMil = dadosEmails?.perThousandCents ?? 0;
    const wa = dadosEnvios === null ? null : totalizarEnvios(dadosEnvios.broadcasts);
    const waAnterior = dadosEnvios === null ? null : totaisAnteriores(dadosEnvios.previous, dadosEnvios.previousPeriod !== null, { companyId: null, senderKind: null });
    const email = dadosEmails === null ? null : totaisDoEmail(dadosEmails.campaigns);
    const atual = combinar(wa, email, porMil);
    const temAnterior = (dadosEnvios?.previousPeriod ?? dadosEmails?.previousPeriod ?? null) !== null;
    const anterior = temAnterior ? combinar(waAnterior, dadosEmails?.previous ?? null, porMil) : null;
    return { atual, anterior };
  }, [dadosEnvios, dadosEmails]);

  const ranking = useMemo<readonly LinhaDoRanking[]>(() => {
    const porMil = dadosEmails?.perThousandCents ?? 0;
    return companies.map((c) => {
      const wa = totalizarEnvios(dadosEnvios?.broadcasts.filter((e) => e.companyId === c.id) ?? []);
      const email = totaisDoEmail(dadosEmails?.campaigns.filter((e) => e.companyId === c.id) ?? []);
      const junto = combinar(wa, email, porMil);
      return {
        companyId: c.id,
        recuperadoCents: junto.recoveredAmountCents,
        comissaoCents: junto.commissionCents,
        pagaram: junto.paid,
        custoPorRecuperacaoCents: porUnidade(junto.costCents, junto.paid),
        envios: junto.broadcasts,
      };
    });
  }, [companies, dadosEnvios, dadosEmails]);

  const insights = useMemo(() => (dadosEnvios === null ? [] : insightsDoHub({ envios: dadosEnvios, emails: dadosEmails, companies })), [dadosEnvios, dadosEmails, companies]);

  const irParaWhatsApp = useCallback(
    (companyId: string, segmento: SegmentKey | null): void => {
      selectCompany(companyId);
      const params = new URLSearchParams(location.search);
      if (segmento === null) {
        params.delete('segmento');
      } else {
        params.set('segmento', segmento);
      }
      const texto = params.toString();
      navigate({ pathname: '/admin/disparos/whatsapp', search: texto === '' ? '' : `?${texto}` });
    },
    [selectCompany, navigate, location.search],
  );

  const executar = (acao: AcaoInsight): void => {
    switch (acao.kind) {
      case 'preparar':
        irParaWhatsApp(acao.companyId, acao.segmentKey);
        return;
      case 'abrir_empresa':
        irParaWhatsApp(acao.companyId, null);
        return;
      case 'rota':
        navigate({ pathname: acao.rota, search: location.search });
        return;
      case 'ancora':
        document.getElementById(acao.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      case 'retomar':
        navigate({ pathname: '/admin/disparos/whatsapp', search: location.search });
        return;
    }
  };

  const periodo = dadosEnvios ?? dadosEmails;
  const dias = periodo === null ? 30 : diasEntre(periodo.periodStart, periodo.periodEnd);
  const projecao = resumo === null ? null : projetar(resumo.atual.recoveredAmountCents, dias, 30);
  const janelaAnterior = dadosEnvios?.previousPeriod ?? dadosEmails?.previousPeriod ?? null;

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="disparo"
        title="Disparos"
        badges={
          <>
            <Badge tone="brand" dot>
              Administração
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description="Envios em massa em nome das empresas, sempre para quem já abandonou, teve o pagamento recusado, venceu ou respondeu sem fechar. Aqui estão o retorno, os canais e o dinheiro parado."
        aside={
          <>
            <RefreshingDot active={(envios.state.status === 'success' && envios.state.refreshing) || (emails.state.status === 'success' && emails.state.refreshing)} />
            <Badge tone="neutral" icon="calendario">
              {periodoTexto}
            </Badge>
          </>
        }
      />

      {simulado ? <SimulatedNotice>Os números são fictícios e fixos, somados para todas as empresas do catálogo, com tarifas de exemplo. Nenhum valor aqui vem do banco nem de fatura.</SimulatedNotice> : null}

      {resumo === null ? (
        envios.state.status === 'loading' || emails.state.status === 'loading' ? (
          <div aria-hidden="true" className="h-56 animate-pulse rounded-2xl border border-line bg-surface" />
        ) : null
      ) : (
        <GradeDeRetorno
          id="retorno-disparos"
          titulo="Retorno dos disparos"
          descricao={`Todas as empresas · WhatsApp e e-mail · ${periodoTexto}, contra o período anterior de mesmo tamanho.`}
          medidas={medidasDeRetorno(resumo.atual, resumo.anterior, null, 'todos')}
          rodape={
            <Footnote>
              {projecao === null ? '' : `No ritmo deste período, 30 dias fecham com cerca de ${formatBRLFromCents(projecao)} recuperados pelos disparos. `}
              {janelaAnterior === null ? 'A janela "desde o início" não tem período anterior.' : `Comparado com ${formatDate(janelaAnterior.start)} a ${formatDate(janelaAnterior.end)}.`}
            </Footnote>
          }
        />
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <CartaoCanal
          tom="whatsapp"
          icon="whatsapp"
          title="Disparos de WhatsApp"
          description="Envios em massa pelo WhatsApp do comercial de cada empresa (Evolution) ou pela API oficial da Meta, com estimativa de custo e retorno antes de disparar."
          bullets={['Públicos pós-fato com histórico de retorno', 'Proteção do número: ritmo, janela e trava de opt-out', 'Respostas caem com as vendedoras; relatório por envio']}
          to="/admin/disparos/whatsapp"
          resumo={<ResumoWhatsApp state={envios.state} />}
        />
        <CartaoCanal
          tom="email"
          icon="email"
          title="Disparos de E-mail"
          description="Escreva, escolha um público pós-fato e dispare; teste de entrega por empresa e prévia no celular. Provedor a definir (Brevo ou Resend)."
          bullets={['Compositor com prévia no computador e no celular', 'Teste de entrega: SPF, DKIM, DMARC e conteúdo', 'Abertura, clique, pagamento e descadastro']}
          to="/admin/disparos/email"
          resumo={<ResumoEmail state={emails.state} />}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <CartaoCanal
          tom="voz"
          icon="telefone"
          title="Ligação da IA em lote"
          description="Ligações da IA para um público escolhido, fora da régua. Depende de uma regra própria de teto de tentativas (RF08) antes de existir."
          bullets={[]}
          badge={<Badge tone="neutral">em breve</Badge>}
          compact
        />
      </div>

      {envios.state.status === 'error' ? <ErrorPanel title="Não foi possível carregar os envios de WhatsApp" error={envios.state.error} onRetry={envios.reload} /> : null}
      {emails.state.status === 'error' ? <ErrorPanel title="Não foi possível carregar as campanhas de e-mail" error={emails.state.error} onRetry={emails.reload} /> : null}

      {dadosEnvios !== null ? (
        <>
          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerAgora
              className="lg:col-span-2"
              insights={insights}
              onAcao={executar}
              descricao="Sugestões derivadas dos disparos de todas as empresas: dinheiro parado, números desconectados, envios pausados, custo e e-mail."
              vazio="Nenhum público grande parado, nenhum número desconectado e nenhum envio pausado. O motor segue sozinho depois de cada abandono ou vencimento."
            />
            <RankingDeEmpresas linhas={ranking} companies={companies} onAbrir={(id) => irParaWhatsApp(id, null)} />
          </div>
          <RadarDeOportunidades oportunidades={dadosEnvios.opportunities} companies={companies} onPreparar={irParaWhatsApp} />
          <p className="text-xs text-ink-subtle">
            Período de {formatDate(dadosEnvios.periodStart)} a {formatDate(dadosEnvios.periodEnd)} ({periodoTexto}) · {formatCount(companies.length)} {companies.length === 1 ? 'empresa' : 'empresas'} ·
            comissão pela regra do ADR-001 (último contato, 7 dias, valor pago). Nenhum disparo fala com quem ainda não abandonou ou venceu.
          </p>
        </>
      ) : null}
    </section>
  );
}

function ResumoWhatsApp({ state }: { readonly state: AsyncState<BroadcastsData> }): JSX.Element {
  if (state.status === 'loading') {
    return <span aria-hidden="true" className="block h-3 w-48 animate-pulse rounded bg-ink/10" />;
  }
  if (state.status === 'error') {
    return <span>Resumo do período indisponível no momento.</span>;
  }
  const t = totalizarEnvios(state.data.broadcasts);
  const unitario = porUnidade(t.costCents, t.paid);
  return (
    <span>
      <span className="font-semibold text-ink">{formatCount(t.broadcasts)}</span> {t.broadcasts === 1 ? 'envio' : 'envios'} ·{' '}
      <span className="font-semibold text-ink">{formatCount(t.delivered)}</span> entregues · <span className="font-semibold text-ink">{formatBRLFromCents(t.recoveredAmountCents)}</span> recuperados ·{' '}
      {unitario === null ? 'sem custo por recuperação' : `${formatBRLFromCents(unitario)} por recuperação`} · <span className="font-semibold text-ink">{formatCount(state.data.optOutTotal)}</span> bloqueados
    </span>
  );
}

function ResumoEmail({ state }: { readonly state: AsyncState<EmailCampaignsData> }): JSX.Element {
  if (state.status === 'loading') {
    return <span aria-hidden="true" className="block h-3 w-48 animate-pulse rounded bg-ink/10" />;
  }
  if (state.status === 'error') {
    return <span>Resumo do período indisponível no momento.</span>;
  }
  const t = totaisDoEmail(state.data.campaigns);
  return (
    <span>
      <span className="font-semibold text-ink">{formatCount(t.campaigns)}</span> {t.campaigns === 1 ? 'campanha' : 'campanhas'} ·{' '}
      <span className="font-semibold text-ink">{formatCount(t.accepted)}</span> aceitos · <span className="font-semibold text-ink">{formatBRLFromCents(t.recoveredAmountCents)}</span> recuperados ·{' '}
      <span className="font-semibold text-ink">{formatCount(state.data.unsubscribeTotal)}</span> descadastros
    </span>
  );
}
